import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { CollectionResponseDto } from '../dto/response/collection-response.dto';
import collectionService from '../services/collection.service';
import { CollectionSaveRequestDto } from '../dto/request/collection-save-request.dto';
import { CollectionUpdateRequestDto } from '../dto/request/collection-update-request.dto';
import { AxiosError } from 'axios';
import { showError, showSuccess } from '../../../../utils/Notifications';
import { LocalesLanguagesConstants } from '../../../../locales/locales';

const initialState = {
  status: 'idle',
  collections: [] as CollectionResponseDto[] | null,
  collection: {} as CollectionResponseDto | null,
  error: null as string | null,
};

export const fetchCollections = createAsyncThunk(
  'collections/fetchCollections',
  async () => {
    return await collectionService.fetchCollections();
  }
);

export const fetchCollectionById = createAsyncThunk(
  'collections/fetchCollectionById',
  async (params: { id: string; language: LocalesLanguagesConstants }) => {
    return await collectionService.fetchCollectionById(
      params.id,
      params.language
    );
  }
);

export const saveCollection = createAsyncThunk(
  'collections/saveCollection',
  async (params: {
    saveRequest: CollectionSaveRequestDto;
    language: LocalesLanguagesConstants;
  }) => {
    return await collectionService.saveCollection(
      params.saveRequest,
      params.language
    );
  }
);

export const updateCollection = createAsyncThunk(
  'collections/updateCollection',
  async (params: {
    updateRequest: CollectionUpdateRequestDto;
    language: LocalesLanguagesConstants;
  }) => {
    return await collectionService.updateCollection(
      params.updateRequest,
      params.language
    );
  }
);

export const deleteCollection = createAsyncThunk(
  'collections/deleteCollection',
  async (existingId: string, { rejectWithValue, dispatch }) => {
    try {
      await collectionService.deleteCollection(existingId);
      dispatch(fetchCollections());
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

const collectionSlice = createSlice({
  name: 'collectionSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCollections.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCollections.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.collections = action.payload;
      })
      .addCase(fetchCollections.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchCollectionById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCollectionById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.collection = action.payload;
      })
      .addCase(fetchCollectionById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(saveCollection.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveCollection.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.collections?.push(action.payload);
        showSuccess('Collection added successfully.');
      })
      .addCase(saveCollection.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(updateCollection.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateCollection.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (state.collections) {
          const collectionIndex = state.collections.findIndex(
            (collection) => collection.id === action.payload.id
          );
          if (collectionIndex !== -1) {
            state.collections[collectionIndex] = action.payload;
          }
        }
      })
      .addCase(updateCollection.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(deleteCollection.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteCollection.fulfilled, (state) => {
        state.status = 'succeeded';
        showSuccess('Collection deleted successfully.');
      })
      .addCase(deleteCollection.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Deleting collection failed. ${action.payload as string}`);
      });
  },
});

export default collectionSlice.reducer;
